import { ApplicationRef, ComponentFactoryResolver, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import * as mapboxgl from "mapbox-gl";
import { TurfHelperService } from "./turf-helper.service";
import { environment } from "../environments/environment";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { BehaviorSubject, Observable, Subscription, catchError, filter, first, map, of } from "rxjs";
import { FeatureCollection, Point, Polygon } from "@turf/helpers";
import { LayerDTO } from "src/app/features/map-features/sections/locations-section/models/LayerDTO";
import { MapUtilService } from "./MapUtil.service";
import { CloropetClases, CloropetConfiguration } from "src/models/CloropetClases";
enum ROUTEMAP_LAYERS {
  Cars = "cars-layer",
  RealRoute = "realRoute-layer",
  Route = "route-layer",
  Geopos = "points-layer",
}

@Injectable({
  providedIn: "root",
})
export class MapHelperService {
  pitch: number = 0;

  // Registro de sources y layers agregados al mapa
  registeredSources: BehaviorSubject<{ name: string, order: number, type: string, sourceDisplayName: string, hasData: boolean, icon: string, displayAttribute: string, canBeSearched: boolean }[]> = new BehaviorSubject([]);
  registeredLayers: BehaviorSubject<{ id: string, order: number, sourceName: string, type: string, layerDisplayName: string }[]> = new BehaviorSubject([]);
  sourceOrderCounter: number = 0;
  layerOrderCounter: number = 0;

  public Empty_GeoJson: any = {
    type: "FeatureCollection",
    features: [],
  };
  mapbox = mapboxgl as typeof mapboxgl;
  Lat: number = 0;
  Lng: number = 0;
  Zoom: number = 0;
  container: string = "map";
  Style: string = "mapbox://styles/mapbox/traffic-day-v2";

  MapStyleName = Object.freeze({
    MapDaylight: "mapbox://styles/danyelgtz/clp0djuiq00wl01qs3vuzgazp",
    MapNight: "mapbox://styles/danyelgtz/cl33z6spb001d16pjou3pqtf5",
    MapSatellite: "mapbox://mapbox.satellite",
  });

  //intersección de capas
  activeLayers: LayerDTO[] = [];  //guarda las capas que están prendidas en los componentes de capas
  
  public featuresInsideArea = new BehaviorSubject< { featureCollection: FeatureCollection<Point>, destination: string } | null>(null);
  featuresInsideArea$ = this.featuresInsideArea.asObservable();

  //variables para obersar carga del mapa
  isMainSourceReady: boolean = false;
  isStyleReady: boolean = false;
  public mainSourceSubject = new BehaviorSubject<{ isMainSourceReady: boolean, isStyleReady: boolean, loadedMapName: string}> (null);
  mainSource$ = this.mainSourceSubject.asObservable();

  _containsControlFullScreen: boolean = false;
  _containsControlNavigation: boolean = false;
  _containsControlGeolocalization: boolean = false;

  geocoder: any;
  public _map: any;

  isPointPickingActive: boolean = false;

  // private mapLoadSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private turfHelperService: TurfHelperService,
    private mapUtilService: MapUtilService
  ) {
    this.mapbox.accessToken = environment.mapBoxToken;
  }

  //#region ------------------ registro de sources y layers para control de capas -------------------------
  
  private registerSource(name: string, type: string, displayName: string, hasData: boolean, icon: string, displayAttribute: string = ''): void {
    if (!this.registeredSources.value.find(s => s.name === name)) {
      this.sourceOrderCounter++;
      this.registeredSources.next([
        ...this.registeredSources.value,
        {
          name,
          order: this.sourceOrderCounter,
          type,
          sourceDisplayName: displayName,
          hasData: hasData,
          icon: icon,
          displayAttribute: displayAttribute,
          canBeSearched: false
        }
      ]);
    }
  }

  updateSourceDataStatus(id, hasData, canBeSearched){
    const currentSource = this.registeredSources.value.find(s => s.name === id);
    if (currentSource) {
      currentSource.hasData = hasData;
      currentSource.canBeSearched = canBeSearched;
      this.registeredSources.next([...this.registeredSources.value]);
    }
  }

  private registerLayer(id: string, sourceName: string, type: string, name: string): void {
    if (!this.registeredLayers.value.find(l => l.id === id)) {
      this.layerOrderCounter++;
      this.registeredLayers.next([
        ...this.registeredLayers.value,
        {
          id,
          order: this.layerOrderCounter,
          sourceName,
          type,
          layerDisplayName: name
        }]);
    }
  }

  getRegisteredSources(): { name: string, order: number,type: string, hasData: boolean, icon: string }[] {
    return [...this.registeredSources.value];
  }

  getRegisteredLayers(): { id: string, order: number, sourceName: string, type: string }[] {
    return [...this.registeredLayers.value];
  }

  getSourceByOrder(order: number): { name: string, order: number, type: string, hasData: boolean, icon: string } | undefined {
    return this.registeredSources.value.find(s => s.order === order);
  }

  getLayerByOrder(order: number): { id: string, order: number, sourceName: string, type: string } | undefined {
    return this.registeredLayers.value.find(l => l.order === order);
  }

  clearSourcesAndLayersRegistry(): void {
    this.registeredSources.next([]);
    this.registeredLayers.next([]);
    this.sourceOrderCounter = 0;
    this.layerOrderCounter = 0;
  }

  removeSourceFromRegistry(name: string): void {
    this.registeredSources.next(this.registeredSources.value.filter(s => s.name !== name));
  }

  removeLayerFromRegistry(id: string): void {
    this.registeredLayers.next(this.registeredLayers.value.filter(l => l.id !== id));
  }

  updateSourcesOrder(orderedSourceNames: string[]): void {
    const totalSources = orderedSourceNames.length;
    const currentSources = this.registeredSources.value;
    const updatedSources = currentSources.map(source => {
      const index = orderedSourceNames.indexOf(source.name);
      
      const newOrder = index !== -1 ? (totalSources - 1 - index) : source.order;
      return { ...source, order: newOrder };
    });
    this.registeredSources.next(updatedSources);

    const currentLayers = this.registeredLayers.value;
    const updatedLayers = currentLayers.map(layer => {
      const sourceIndex = orderedSourceNames.indexOf(layer.sourceName);
      const newOrder = sourceIndex !== -1 ? (totalSources - 1 - sourceIndex) : layer.order;
      return { ...layer, order: newOrder };
    });
    this.registeredLayers.next(updatedLayers);
  }

  //#endregion

  //#region ------------------ eventos abstractos del mapa -------------------------
  addEventOnMapLoad(callback: () => void): void {
    if (this._map) {
      if (this._map.loaded()) {
        callback();
      } else {
        this._map.on("load", () => callback());
      }
    }
  }

  addEventOnMapClick(callback: (event: any) => void): void {
    if (this._map) {
      this._map.on("click", (event) => callback(event));
    }
  }

  addEventOnMap(eventType: string, callback: (event?: any) => void): void {
    if (this._map) {
      this._map.on(eventType, callback);
    }
  }

  RemoveEventFromMap(eventType: string, callback: (event?: any) => void): void {
    if (this._map) {
      this._map.off(eventType, callback);
    }
  }

  AddLayerEvent(eventType: string, layerName: string, callback: (event: any) => void): void {
    if (this._map) {
      this._map.on(eventType, layerName, callback);
    }
  }

  RemoveLayerEvent(eventType: string, layerName: string, callback: (event: any) => void): void {
    if (this._map) {
      this._map.off(eventType, layerName, callback);
    }
  }
  //#endregion

  //#region ------------------ controles abstractos del mapa -------------------------
  addControl(control: any): void {
    if (this._map) {
      this._map.addControl(control);
    }
  }

  removeControl(control: any): void {
    if (this._map) {
      try {
        this._map.removeControl(control);
      } catch (error) {}
    }
  }

  hasControl(control: any): boolean {
    if (this._map) {
      try {
        return this._map._controls.includes(control);
      } catch (error) {
        return false;
      }
    }
    return false;
  }
  //#endregion

  //#region ------------------ eventos de herramienta de dibujo (Draw) -------------------------
  onDrawCreate(callback: (event: any) => void): void {
    if (this._map) {
      this._map.on("draw.create", callback);
    }
  }

  offDrawCreate(callback: (event: any) => void): void {
    if (this._map) {
      this._map.off("draw.create", callback);
    }
  }

  onDrawUpdate(callback: (event: any) => void): void {
    if (this._map) {
      this._map.on("draw.update", callback);
    }
  }

  offDrawUpdate(callback: (event: any) => void): void {
    if (this._map) {
      this._map.off("draw.update", callback);
    }
  }

  onDrawDelete(callback: (event: any) => void): void {
    if (this._map) {
      this._map.on("draw.delete", callback);
    }
  }

  offDrawDelete(callback: (event: any) => void): void {
    if (this._map) {
      this._map.off("draw.delete", callback);
    }
  }

  onDrawSelectionChange(callback: (event: any) => void): void {
    if (this._map) {
      this._map.on("draw.selectionchange", callback);
    }
  }

  offDrawSelectionChange(callback: (event: any) => void): void {
    if (this._map) {
      this._map.off("draw.selectionchange", callback);
    }
  }

  //#endregion

  //#region ------ mapa base -------
  createBaseMap(lat, lng, zoom) {
    this.Lat = lat;
    this.Lng = lng;
    this.Zoom = zoom;

    this._map = new mapboxgl.Map({
      container: this.container,
      style: this.Style,
      zoom: this.Zoom,
      pitch: 0,
      center: [this.Lng, this.Lat],
    });
    
    const mapContainer = document.getElementById(this.container);
    if (mapContainer) {
      mapContainer.style.userSelect = 'none';
      mapContainer.style.webkitUserSelect = 'none';
    }
    this.fitBoundsToMexico();
  }

  StartPresentationMode(){
    this.InitialFly(  this.Lat.toString(),  this.Lng.toString(), this.Zoom.toString(), 5000  );
    this.TourCamera(7000);
    this.FlyToCamera(this.Lat.toString(), this.Lng.toString(), this.Zoom.toString());
  }

  ShowHideBaseMapSatellite() {
      this.ShowHideLayer("satellite-layer");
  }


  //#endregion

  //#region ------ gestión de estado del mapa -------
  setMapSourcesAsReady(loadedMapName: string){
    this.isMainSourceReady = true;
    this.mainSourceSubject.next({
        isMainSourceReady: this.isMainSourceReady,
        isStyleReady: this.isStyleReady,
        loadedMapName: loadedMapName
    });
  }

  AddMapStyleLoadVerifier(loadedMapName: string){
    //debe de mandar que los estilos estan listos UNA vez, pero por si a caso, se agregaron pipes con filtros y take en donde se usa la subscripción
    this._map.on("styledata", () => {
      var isStyleFullyLoaded = false;
      try {
        isStyleFullyLoaded = this._map?.isStyleLoaded() && this._map?.areTilesLoaded() 
      } catch (e) {
        isStyleFullyLoaded = false;
      }
      // if(!environment.production) console.log("estilo 3", isStyleFullyLoaded, this._map?.isStyleLoaded(), this._map?.areTilesLoaded());
      if (isStyleFullyLoaded && !this.isStyleReady) {
        this.isStyleReady = true;
        this.mainSourceSubject.next({
          isMainSourceReady: this.isMainSourceReady,
          isStyleReady: this.isStyleReady,
          loadedMapName: loadedMapName
        });
      }
    });

    this._map.on("idle", () => {
      if (!this.isStyleReady) {
        this.isStyleReady = true;
        this.mainSourceSubject.next({
          isMainSourceReady: this.isMainSourceReady,
          isStyleReady: this.isStyleReady,
          loadedMapName: loadedMapName
        });
      }
    });
  }

  getMainSource(){
    return this.mainSourceSubject.asObservable();
  }

  AddBaseMapSatellite() {
    var layer: mapboxgl.Layer = this._map.getLayer(
      "satellite-layer"
    ) as mapboxgl.Layer;
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: "satellite-layer",
          source: {
            type: "raster",
            url: this.MapStyleName.MapSatellite,
            tileSize: 256,
          },
          type: "raster",
        });
      }
    } catch (e) {}
  }

  removeMap(loadedMapName: string){
    if(this._map){
      this.isStyleReady = false;
      this.isMainSourceReady = false;
      this.clearSourcesAndLayersRegistry();
      this.mainSourceSubject.next({
        isMainSourceReady: this.isMainSourceReady,
        isStyleReady: this.isStyleReady,
        loadedMapName: loadedMapName
      });
    }
  }

  waitForMapToRender(): Promise<void> {
    return new Promise((resolve) => {
      if (!this._map) {
        resolve();
        return;
      }

      // cambie de idle a load y se agrgeo esta otra verificacion, el idle daba problemas
      if (this._map.isStyleLoaded() && this._map.loaded()) {
        setTimeout(() => resolve(), 100);
        return;
      }

      // timeout de seguridad por si el load no se dispara
      const timeout = setTimeout(() => {
        this._map.off('load', onLoaded);
        resolve();
      }, 3000);

      const onLoaded = () => {
        clearTimeout(timeout);
        this._map.off('load', onLoaded);
        setTimeout(() => resolve(), 100);
      };

      this._map.once('load', onLoaded);
    });
  }

  mapResize(): void {
    if (this._map) {
      this._map.resize();
    }
  }
  //#endregion

  //#region ------ añadir layers -------

  AddLayerCluster(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "circle",
          source: source,
          filter: ["has", "point_count"],
          paint: {
            // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
            // with three steps to implement three types of circles:
            //   * Blue, 20px circles when point count is less than 100
            //   * Yellow, 30px circles when point count is between 100 and 750
            //   * Pink, 40px circles when point count is greater than or equal to 750
            "circle-color": [
              "step",
              ["get", "point_count"],
              "#51bbd6",
              100,
              "#f1f075",
              750,
              "#f28cb1",
            ],
            "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
          },
        });
        this.registerLayer(id, source, 'circle', layerDisplayName);
      }
    } catch (e) {}
  }

  AddClusterZoomEvent(clusterLayerName: string, sourceName: string) {
    const map = this._map;
    if (!map)
      return;

    const onClusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [clusterLayerName] });
      if (!features || features.length === 0)
        return;

      const clusterId = features[0].properties.cluster_id;
      const source: any = map.getSource(sourceName);
      if (!source || !source.getClusterExpansionZoom)
        return;

      source.getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error)
          return;
        map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
      });
    };

    const onClusterEnter = () => { map.getCanvas().style.cursor = "pointer"; };
    const onClusterLeave = () => { map.getCanvas().style.cursor = ""; };

    map.off("click", clusterLayerName, onClusterClick);
    map.on("click", clusterLayerName, onClusterClick);
    map.off("mouseenter", clusterLayerName, onClusterEnter);
    map.on("mouseenter", clusterLayerName, onClusterEnter);
    map.off("mouseleave", clusterLayerName, onClusterLeave);
    map.on("mouseleave", clusterLayerName, onClusterLeave);
  }

  AddLayerPolygons(id: string, source: string, layerDisplayName: string) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-outline-color": "rgba(0,0,0,0.1)",
            "fill-color": "rgba(231,126,45,0.2)",
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerHeatmap(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          maxzoom: 9,
          paint: {
            // 'fill-color': [
            // 'interpolate',
            // ['linear'],
            // ['get', 'valor'],
            // 0,
            // '#c90076',
            // 50,
            // '#c90076',
            // 100,
            // '#c90076',
            // 150,
            // '#c90076',
            // 200,
            // '#c90076',
            // 250,
            // '#c90076',
            // 300,
            // '#c90076',
            // 500,
            // '#c90076',
            // 1000,
            // '#c90076'
            // ],
            "fill-color": "#0080ff",
            "fill-opacity": 0.75,
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerGeofencesPolygons(id: string, source: string, layerDisplayName: string, color: string = "#808080") {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-color": [
              "match",
              ["get", "Status"],
                "1",
                "rgb(157, 224, 42)", // Si Status es 1, usa este color
                "0",
                "rgb(128, 128, 128)", // Si Status es 0, usa este color
                color, // Color predeterminado si no se cumple ninguna condición o viene de afuera
            ],
            "fill-opacity": [
              "match",
              ["get", "Status"],
                "1",
                0.4, // Si Status es 1 o 0, se ve
                "0",
                0.4,
                0,
            ],
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerCustomizablePolygons(id: string, source: string, opacity: number, layerDisplayName: string, color: string = "#6630fd", maxZoom: number = 22, minZoom: number = 0) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-color": [
              "match",
              ["get", "Status"],
                "1",
                color,
                "0",
                "#808080", 
                color, 
            ],
            "fill-opacity": opacity,
          },
          maxzoom: maxZoom,
          minzoom: minZoom,
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  // AddLayerPolygonsWithFeatureStatus(id: string, source: string, color: string = "#808080") {
  //   var layer = this._map.getLayer(id);
  //   try {
  //     if (layer === undefined) {
  //       this._map.addLayer({
  //         id: id,
  //         type: "fill",
  //         source: source,
  //         paint: {
  //           "fill-color": [
  //             "case",
  //             ['boolean', ['feature-state', 'selected'], false],
  //               "#f25d18", 
  //               color,
  //           ],
  //           "fill-opacity": [
  //             "case",
  //             ['boolean', ['feature-state', 'hover'], false],
  //               0.6,
  //               0.4,
  //           ],
  //         },
  //       });
  //     }
  //   } catch (e) {}
  // }

  AddLayerPolygonsWithFeatureStatus(id: string, source: string, layerDisplayName: string, color: string = "#808080", cloropetConfig: CloropetConfiguration = null) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        let fillColors: any;
        
        if (cloropetConfig && cloropetConfig.clases && cloropetConfig.clases.length > 0) {
          const matchExpression: any[] = [
            "match",
            ["get", cloropetConfig.propertyName]
          ];
          cloropetConfig.clases.forEach(clase => {
            matchExpression.push(clase.classProperty);
            matchExpression.push(clase.classValue);
          });
          matchExpression.push(color);
          
          fillColors = [
            "case",
            ['boolean', ['feature-state', 'selected'], false],
              "#3a3a3a",
              matchExpression
          ];
        } else {
          fillColors = [
            "case",
            ['boolean', ['feature-state', 'selected'], false],
              "#3a3a3a", 
              color,
          ];
        }

        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-color": fillColors,
            "fill-opacity": [
              "case",
              ['boolean', ['feature-state', 'hover'], false],
                0.6,
                0.4,
            ],
            "fill-outline-color": "#000000",
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerPatternPolygonsWithFeatureStatus(id: string, source: string, layerDisplayName: string, color: string = "#808080", cloropetConfig: CloropetConfiguration = null) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        let fillColors: any;
        
        // if (cloropetConfig && cloropetConfig.clases && cloropetConfig.clases.length > 0) {
        //   const matchExpression: any[] = [
        //     "match",
        //     ["get", cloropetConfig.propertyName]
        //   ];
        //   cloropetConfig.clases.forEach(clase => {
        //     matchExpression.push(clase.classProperty);
        //     matchExpression.push(clase.classValue);
        //   });
        //   matchExpression.push(color);
          
        //   fillColors = [
        //     "case",
        //     ['boolean', ['feature-state', 'selected'], false],
        //       "#3a3a3a",
        //       matchExpression
        //   ];
        // } else {
        //   fillColors = [
        //     "case",
        //     ['boolean', ['feature-state', 'selected'], false],
        //       "#3a3a3a", 
        //       color,
        //   ];
        // }

        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-pattern": "diagonal_l",
            "fill-opacity": [
              "case",
              ['boolean', ['feature-state', 'hover'], false],
                0.6,
                0.4,
            ],
            "fill-outline-color": "#000000",
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerPolygonsForTerritoryStatus(id: string, source: string, layerDisplayName: string) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        let fillColors: any;
        
        fillColors = [
          "case",
          ['boolean', ['feature-state', 'selected'], false],
            "rgb(58, 58, 58)", 
            ['get', 'color'],
        ];

        this._map.addLayer({
          id: id,
          type: "fill",
          source: source,
          paint: {
            "fill-color": fillColors,
            "fill-opacity": [
              "case",
              ['boolean', ['feature-state', 'hover'], false],
                0.6,
                0.4,
            ],
            "fill-outline-color": "#000000",
          },
        });
        this.registerLayer(id, source, 'fill', layerDisplayName);
      }
    } catch (e) {}
  }

  // AddLayerPolygonsForTerritoryStatus(id: string, source: string, layerDisplayName: string, color: string = "#808080", cloropetConfig: CloropetConfiguration = null) {
  //   var layer = this._map.getLayer(id);
  //   try {
  //     if (layer === undefined) {
  //       let fillColors: any;
        
  //       if (cloropetConfig && cloropetConfig.clases && cloropetConfig.clases.length > 0) {
  //         let colorExpression: any[];
          
  //         if (cloropetConfig.usePercentageRanges) {
  //           colorExpression = [
  //             "step",
  //             ["get", cloropetConfig.propertyName]
  //           ];
            
  //           const sortedClases = [...cloropetConfig.clases].sort((a, b) => 
  //             Number(a.classProperty) - Number(b.classProperty)
  //           );

  //           colorExpression.push(sortedClases[0].classValue);

  //           for (let i = 1; i < sortedClases.length; i++) {
  //             colorExpression.push(Number(sortedClases[i].classProperty));
  //             colorExpression.push(sortedClases[i].classValue);
  //           }
  //         } else {
  //           colorExpression = [
  //             "match",
  //             ["get", cloropetConfig.propertyName]
  //           ];
  //           cloropetConfig.clases.forEach(clase => {
  //             colorExpression.push(clase.classProperty);
  //             colorExpression.push(clase.classValue);
  //           });
  //           colorExpression.push(color);
  //         }
          
  //         fillColors = [
  //           "case",
  //           ['boolean', ['feature-state', 'selected'], false],
  //             "#3a3a3a",
  //             colorExpression
  //         ];
  //       } else {
  //         fillColors = [
  //           "case",
  //           ['boolean', ['feature-state', 'selected'], false],
  //             "#3a3a3a", 
  //             color,
  //         ];
  //       }

  //       this._map.addLayer({
  //         id: id,
  //         type: "fill",
  //         source: source,
  //         paint: {
  //           "fill-color": fillColors,
  //           "fill-opacity": [
  //             "case",
  //             ['boolean', ['feature-state', 'hover'], false],
  //               0.6,
  //               0.4,
  //           ],
  //           "fill-outline-color": "#000000",
  //         },
  //       });
  //       this.registerLayer(id, source, 'fill', layerDisplayName);
  //     }
  //   } catch (e) {}
  // }

  AddLayerIconSmall(id: string, source: string, icon: string, layerDisplayName: string) {
    if(!environment.production) console.log("Loading layer icon");
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          layout: {
            "icon-allow-overlap": true,
            "icon-image": ["coalesce", ["get", "LocationCategoryIcon"], icon],
            "icon-size": 0.08,
          },
          paint: {
            "icon-color": "#e57620",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {
      if(!environment.production) console.log(e);
    }
  }
  AddLayerUnclusteredPoints(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-allow-overlap": true,
            "icon-image": "car",
            "icon-size": 0.15,
          },
          paint: {
            "icon-color": "#22cbb6",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerUnclusteredPointsV2(id: string, source: string, icon: string, layerDisplayName: string, iconSize: number = 0.10) {
    var layoutLayer = {};
    if(icon){
      layoutLayer = {
        "icon-allow-overlap": false,
        "icon-image": ["coalesce", ["get", "LocationCategoryIcon"], icon],
        "icon-size": iconSize,
      };
    }else{
      layoutLayer = {
        "icon-allow-overlap": false,
        "icon-image": ["get", "Icon"],
        "icon-size": iconSize,
      };
    }
    
    // var iconImage = icon.replace(".png", "");
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: layoutLayer,
          paint: {
            "icon-color": "#22cbb6",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerGeofencesPinRadar(id: string, source: string, layerDisplayName: string) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          maxzoom: 10,
          layout: {
            "icon-allow-overlap": true,
            "icon-image": [
              "case",
              ["==", ["get", "Status"], "1"], // Si Status es igual a 1
              "pulsingDot", // Usar el icono "fence"
              ["==", ["get", "Status"], "0"], // Si Status es igual a 0
              "pulsingDot",
              "", // Si no coincide con 1 ni con 0, no se muestra ningún icono
            ],
            "icon-size": 0.15,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerGeofencesByStatus(id: string, source: string, layerDisplayName: string) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          maxzoom: 13,
          layout: {
            "icon-allow-overlap": true,
            "icon-image": [
              "case",
              ["==", ["get", "Status"], "1"], // Si Status es igual a 1
              "geofence.png", // Usar el icono "fence"
              ["==", ["get", "Status"], "0"], // Si Status es igual a 0
              "geofenceInactive.png",
              "geofenceInactive.png", // Si no coincide con 1 ni con 0, no se muestra ningún icono
            ],
            "icon-size": 0.15,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }
  AddCirclesLayerWithHex(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "circle",
          source: source,
          paint: {
            "circle-radius": 5,
            "circle-color": [
              "case",
              ["==", ["feature-state", "selected"], true], 
              "#00FF00",
              ["==", ["feature-state", "hover"], true],
              "#111111",
              ["get", "color"],
            ],
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
            "circle-stroke-opacity": 1,
          },
        });
        this.registerLayer(id, source, 'circle', layerDisplayName);
      }
    } catch (e) {}
  }
  AddUnclusteredCirclesLayerWithHex(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "circle",
          source: source,
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-radius": 10,
            "circle-color": ["get", "color"],
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
            "circle-stroke-opacity": 1,
          },
        });
        this.registerLayer(id, source, 'circle', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerLocationActive(id: string, source: string, categoryActiveIcon: string, layerDisplayName: string, iconSize: number = 0.25) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        var image = categoryActiveIcon;
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            // This icon is a part of the Mapbox Streets style.
            // To view all images available in a Mapbox style, open
            // the style in Mapbox Studio and click the "Images" tab.
            // To add a new image to the style at runtime see
            // https://docs.mapbox.com/mapbox-gl-js/example/add-image/
            'icon-image': ["coalesce", ["get", "LocationCategoryActiveIcon"], image],
            "icon-size": iconSize,
            // 'icon-offset': [175, -175]
          },
          paint: {
            "icon-color": "#7DFF45",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerLocationsGeneral(id: string, source: string, iconProperty: string, layerDisplayName: string, iconSize: number = 0.15, allowOverlap: boolean = false) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            'icon-image': ['get', iconProperty],
            "icon-size": iconSize,
            "icon-allow-overlap": allowOverlap,
            // 'icon-offset': [175, -175]
          },
          paint: {
            "icon-color": "#7DFF45",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerIcon(id: string, source: string, icon: string, layerDisplayName: string, iconSize: number = 0.15, offset: number[] = [0,0]) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            // This icon is a part of the Mapbox Streets style.
            // To view all images available in a Mapbox style, open
            // the style in Mapbox Studio and click the "Images" tab.
            // To add a new image to the style at runtime see
            // https://docs.mapbox.com/mapbox-gl-js/example/add-image/
            'icon-image': ["coalesce", ["get", "LocationCategoryIcon"], icon],
            "icon-size": iconSize,
            "icon-offset": offset
          },
          paint: {
            "icon-color": "#7DFF45",
            "icon-halo-color": "#fff",
            "icon-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }


  AddLayerText(id: string, source: string, propiedadDisplay: string, layerDisplayName: string, offsetY: number, offsetX: number = 0, textSize: number = 12) {
    if(!environment.production) console.log("ADDING ", id, source, propiedadDisplay);
    var layer = this._map.getLayer(id);
    try {
      if (layer == undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          layout: {
            "text-field": ["case",
              ["has", propiedadDisplay],
              ["get", propiedadDisplay],
              ["get", "Description"]],
            "text-font": ["Open Sans Regular"],
            "text-size": textSize,
            "text-offset": [offsetX, offsetY],
            "text-anchor": "bottom",
            "text-justify": "center",
            "text-max-width": 10,
            "text-line-height": 1.2,
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            "symbol-placement": "point",
          },
          paint: {
            "text-color": "#530aa8",
            "text-halo-color": "#fff",
            "text-halo-width": 2,
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {
      if(!environment.production) console.log(e);
    }
  }

  AddLayerTextH3(id: string, source: string, propiedadDisplay: string, layerDisplayName: string) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-field": ["get", propiedadDisplay],
            "text-font": ["Open Sans Bold"],
            "text-size": 20,
            "text-offset": [0, -2],
            "text-anchor": "bottom",
          },
          paint: {
            "text-color": "#537aa8",
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerTextH3WithStatus(
    id: string,
    source: string,
    propiedadDisplay: string,
    layerDisplayName: string
  ) {
    var layer = this._map.getLayer(id);
    try {
      if (layer === undefined) {
        this._map.addLayer({
          id: id,
          type: "symbol",
          source: source,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-field": [
              "case",
              ["==", ["get", "Status"], "-1"],
              "", // Si status es igual a -1 de la geocerca, establece el campo de texto como una cadena vacía
              ["get", propiedadDisplay],
            ],
            "text-font": ["Open Sans Bold"],
            "text-size": 15,
            "text-offset": [0, -2],
            "text-anchor": "bottom",
          },
          paint: {
            "text-color": "#530aa8",
          },
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerPointActive(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "symbol",
      source: source,
      filter: ["!", ["has", "point_count"]],
      layout: {
        // This icon is a part of the Mapbox Streets style.
        // To view all images available in a Mapbox style, open
        // the style in Mapbox Studio and click the "Images" tab.
        // To add a new image to the style at runtime see
        // https://docs.mapbox.com/mapbox-gl-js/example/add-image/
        "icon-image": "buffer-active",
        "icon-size": 0.1,
      },
      paint: {
        "icon-color": "#7DFF45",
        "icon-halo-color": "#fff",
        "icon-halo-width": 2,
      },
    });
    this.registerLayer(id, source, 'symbol', layerDisplayName);
  }

  AddLayerBufferActive(id: string, source: string, type: number, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      source: source,
      type: "fill",
      paint: {
        "fill-color": ["case",
          ["==", type, 1], // Si el tipo es igual a 1 es un buffer de geofence
          "rgb(242, 93, 24)",
          ["==", type, 2], // Si el tipo es igual a 2 es un buffer de valet parking
          "rgb(242, 242, 24)",
          "#f25d18"
        ],
        "fill-opacity": 0.4,
      },
    });
    this.registerLayer(id, source, 'fill', layerDisplayName);
  }

  AddLayerBufferRecurrent(id: string, source: string, type: number, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      source: source,
      type: "fill",
      paint: {
        "fill-color": [
          "case",
          ["has", "color"],  // Si la propiedad 'color' existe
          ["get", "color"],
          "#f25d18"           // default
        ],
        "fill-opacity": 0.4,
      },
    });
    this.registerLayer(id, source, 'fill', layerDisplayName);
  }

  AddLayerClusterCount(name: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(name);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: name,
          type: "symbol",
          source: source,
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
        });
        this.registerLayer(name, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  addLayerCenterPoint(id: string, source: string, layerDisplayName: string) {
    var existing = this._map.getLayer(id);
    try {
      if (existing === undefined) {
        this._map.addLayer({
          id: id,
          type: 'symbol',
          source: source,
          layout: {
            'icon-image': 'centerPin.png',
            'icon-size': 0.1,
            'icon-anchor': 'center',
            'icon-offset': [0, -200]
          }
        });
        this.registerLayer(id, source, 'symbol', layerDisplayName);
      }
    } catch (e) {}
  }

  AddLayerLineDashed(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#352F84",
        "line-dasharray": [2, 1],
        "line-width": 5,
        "line-opacity": 0.85,
      },
    });
    this.registerLayer(id, source, 'line', layerDisplayName);
  }
  AddLayerLine(id: string, source: string,  layerDisplayName: string,  lineColor: string = "rgb(53, 47, 132)", lineWidth: number = 5) {
    this._map.addLayer({
      id: id,
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": lineColor,
        "line-width": lineWidth,
        "line-opacity": 0.75,
      },
    });
    this.registerLayer(id, source, 'line', layerDisplayName);
  }

  AddDirectionLine(id: string, source: string,  displayName: string, lineColor: string = "#7c1779") {
    this.AddSourceEmptyGeoJson(source, displayName, 'annotation-line.png');
    this.AddSourceEmptyGeoJson(source+ "-Start", "Punto de inicio de " + displayName, 'annotation-routeSheet-start-point.png');
    this.AddSourceEmptyGeoJson(source+ "-End", "Punto de fin de " + displayName, 'endPoint-flag.png');
    this.AddSourceEmptyGeoJson(source+"-Dialog", "Diálogo de " + displayName, 'routeBalloon-offCenter.png');

    this._map.addLayer({
      id: id + "-background",
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": "#fff",
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 5,
          15, 6,
          18, 10
        ],
        "line-opacity": 0.8,
      },
    });
    this.registerLayer(id + "-background", source, 'line', "Fondo de " + displayName);
  
    this._map.addLayer({
      id: id,
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": lineColor,
        // "line-gradient": [
        //   "interpolate",
        //   ["linear"],
        //   ["line-progress"],
        //   0, "#972BFB",
        //   1, "#7A49FF"
        // ],
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 3,
          15, 5,
          18, 8
        ],
        "line-opacity": 1,
      },
    });
    this.registerLayer(id, source, 'line', "Linea de " + displayName);
  
    this._map.addLayer({
      id: id + "-arrows",
      type: "symbol",
      source: source,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 5,
          15, 10,
          18, 20
        ],
        "icon-image": "white-arrow.png",
        "icon-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 0.04,
          15, 0.025,
          18, 0.032
        ],
        "icon-rotation-alignment": "map",
        "icon-pitch-alignment": "map",
        // "icon-rotate": 90,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
      "icon-opacity": 1.0
      }
    });
    this.registerLayer(id + "-arrows", source, 'symbol', "Flechas de " + displayName);

    this._map.addLayer({
      id: id + "-start-pin-pulse",
      type: "symbol",
      source: source + "-Start",
      layout: {
        "icon-image": "startPoint-pulse",
        "icon-size": 0.6,
        "icon-anchor": "top",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-pitch-alignment": "map",
        "icon-rotation-alignment": "map",
        "icon-offset": [-2, -22],
      },
      paint: {
        "icon-opacity": 0.7
      }
    });
    this.registerLayer(id + "-start-pin-pulse", source + "-Start", 'symbol', "Animacion de pin de inicio de " + displayName);

    // Pin de inicio
    this._map.addLayer({
      id: id + "-start-pin",
      type: "symbol",
      source: source+"-Start",
      layout: {
        "icon-image": "startPoint.png",
        "icon-size": 0.06011,
        "icon-offset": [0, -10],
        "icon-anchor": "top",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-pitch-alignment": "map",
        "icon-rotation-alignment": "map",
      },
      paint: {
        "icon-opacity": 1.0
      }
    });
    this.registerLayer(id + "-start-pin", source + "-Start", 'symbol', "Pin de inicio de " + displayName);
  
    // Pin de llegada  
    this._map.addLayer({
      id: id + "-end-pin",
      type: "symbol",
      source: source+ "-End",
      layout: {
        "icon-image": "endPoint-flag.png",
        // "icon-size": 0.20,
        "icon-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 0.2,
          15, 0.2,
          18, 0.00
        ],
        "icon-anchor": "bottom",
        "icon-offset": [-1, -10],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-opacity": 1.0
      }
    });
    this.registerLayer(id + "-end-pin", source + "-End", 'symbol', "Pin de fin de " + displayName);
  
    // Texto al lado de la ruta (de momento no)
    this._map.addLayer({
      id: id + "-route-label",
      type: "symbol",
      source: source,
      layout: {
        "symbol-placement": "point",
        "symbol-spacing": 999999, 
        "text-field": "", 
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 14,
        "text-anchor": "center",
        "icon-pitch-alignment": "viewport",
        "icon-rotation-alignment": "viewport",
        "text-offset": [0, -2],
        "text-justify": "center"
      },
      paint: {
      "text-color": "#fff",
      "text-halo-color": "#7C3AED",
      "text-halo-width": 8
    }
    });
    this.registerLayer(id + "-route-label", source, 'symbol', "Texto de " + displayName);

    this._map.addLayer({
      id: id + "-dialog",
      type: "symbol",
      source: source + "-Dialog",
      layout: {
        "icon-image": "routeBalloon-offCenter.png",
        "icon-size": 0.10,
        "icon-anchor": "center",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true
      },
      paint: {
        "icon-opacity": 0
      }
    });
    this.registerLayer(id + "-dialog", source + "-Dialog", 'symbol', "Dialogo de " + displayName);

    this._map.addLayer({
      id: id + "-dialog-text",
      type: "symbol",
      source: source + "-Dialog",
      layout: {
        "text-field": ["get", "RTA"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 10,
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-offset": [1.5, -4],
        "text-max-width": 6
      },
      paint: {
        "text-color": "#fff",
        // "text-halo-color": "#000",
        "text-halo-width": 2,
        "icon-opacity": 0
      }
    });
    this.registerLayer(id + "-dialog-text", source + "-Dialog", 'symbol', "Texto de dialogo de " + displayName);
  }


  AddLayerLineBold(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": "rgb(184, 5, 255)",
        "line-dasharray": [2, 1],
        "line-width": 8,
        "line-opacity": 0.85,
      },
    });
    this.registerLayer(id, source, 'line', layerDisplayName);
  }

  AddLayerLineOpacityMin(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "line",
      source: source,
      layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": "rgb(56, 135, 190)",
        "line-width": 7,
        "line-opacity": 0.35,
      },
    });
    this.registerLayer(id, source, 'line', layerDisplayName);
  }

  AddLayerSteps(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "symbol",
      source: source,
      maxzoom: 22,
      layout: {
        "icon-allow-overlap": true,
        "icon-image": [
          "case",
          ["==", ["get", "StatusVehicle"], "Encendido"],
          "starting",
          ["==", ["get", "StatusVehicle"], "Apagado"],
          "parking",
          ["==", ["get", "StatusVehicle"], "Moviendo"],
          "moving",
          ["==", ["get", "StatusVehicle"], "Reportando"],
          "reporting",
          "",
        ],
        "icon-size": 0.15,
      },
    });
    this.registerLayer(id, source, 'symbol', layerDisplayName);
  }

  AddLayerGeoPoints(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "symbol",
      source: source,
      layout: {
        "icon-image": "pinGeoposition",
        "icon-size": 0.15,
        "text-field": ["get", "_NoOrder"],
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-offset": [0, 1.25],
        "text-anchor": "top",
      },
      paint: {
        "icon-color": [
          "match",
          ["get", "_IconValue"],
          "1",
          "#008000",
          "2",
          "#FF0000",
          "#f5290a",
        ],
      },
    });
    this.registerLayer(id, source, 'symbol', layerDisplayName);
  }

  AddLayerStartEndPoints(id: string, source: string, layerDisplayName: string) {
    this._map.addLayer({
      id: id,
      type: "symbol",
      source: source,
      layout: {
        "icon-image": "pin",
        "icon-size": 0.3,
        "text-field": ["get", "_NoOrder"],
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-offset": [0, 1.25],
        "text-anchor": "top",
      },
      paint: {
        "icon-color": [
          "match",
          ["get", "_IconValue"],
          "1",
          "#008000",
          "2",
          "#FF0000",
          "#9ACD32",
        ],
      },
    });
    this.registerLayer(id, source, 'symbol', layerDisplayName);
  }

  //#endregion

//#region ------------------ manipulación de capas -------------------------

  GetLayerByName(name: string): mapboxgl.Layer | undefined {
    return this._map.getLayer(name);
  }

  getLayerFeatures(layerId: string) : any {
    const renderedFeatures = this._map.queryRenderedFeatures({ layers: [layerId] });
  
    //elimina duplicados con el array y despues obtiene los values
    const uniqueFeatures = [ ...new Map(renderedFeatures.map(f => [f.id, f])).values() ];
    if(!environment.production) console.log("getLayerFeatures", renderedFeatures, uniqueFeatures);
    
    return uniqueFeatures;
  }

  ShowHideLayer(layerName: any) {
    const visibility = this._map.getLayoutProperty(layerName, "visibility");

    // Toggle layer visibility by changing the layout object's visibility property.
    if (visibility === "visible") {
      this._map.setLayoutProperty(layerName, "visibility", "none");
    } else {
      this._map.setLayoutProperty(layerName, "visibility", "visible");
    }
  }

  HideLayer(layerName: string) {
    var layer: mapboxgl.Layer = this._map.getLayer(layerName) as mapboxgl.Layer;
    if (layer !== undefined) {
      this._map.setLayoutProperty(layerName, "visibility", "none");
    }
  }

  HideLayersBySource(sourceName: string) {
    const layers = this._map.getStyle().layers.filter(l => l.source === sourceName);
    layers.forEach(layer => {
      this._map.setLayoutProperty(layer.id, "visibility", "none");
    });
  }

  ShowLayersBySource(sourceName: string) {
    const layers = this._map.getStyle().layers.filter(l => l.source === sourceName);
    layers.forEach(layer => {
      this._map.setLayoutProperty(layer.id, "visibility", "visible");
    });
  }

  ShowLayer(layerName: string) {
    var layer: mapboxgl.Layer = this._map.getLayer(layerName) as mapboxgl.Layer;
    if (layer !== undefined) {
      this._map.setLayoutProperty(layerName, "visibility", "visible");
    }
  }
  MoveLayer(layerToMove: string, destinationLayer: string) {
    var layer: mapboxgl.Layer = this._map.getLayer(layerToMove) as mapboxgl.Layer;
    if (layer !== undefined) {
      this._map.moveLayer(layerToMove, destinationLayer);
    }
  }
  MoveLayerToTop(layerName: string) {
    var layer: mapboxgl.Layer = this._map.getLayer(layerName) as mapboxgl.Layer;
    if (layer !== undefined) {
      this._map.moveLayer(layerName);

      const layerEntry = this.registeredLayers.value.find(l => l.id === layerName);
      if (layerEntry) {
        const sourceName = layerEntry.sourceName;
        const otherSources = this.registeredSources.value.filter(source => source.name !== sourceName).sort((a, b) => b.order - a.order).map(s => s.name);
        this.updateSourcesOrder([sourceName, ...otherSources]);
      }
    }
  }

  UpdateLayerGeoJson(id: string, Data: any) {
    try {
      var SOURCE: mapboxgl.GeoJSONSource = this._map.getSource(
        id
      ) as mapboxgl.GeoJSONSource;
      if(!environment.production) console.log("Sustituyendo Geojson", id, SOURCE);
      if (SOURCE === undefined) {
        // this.initializeMapRoutes();
        // this.initializeMapGeofences();
        // this.initializeMapLocations();
      }
      if (SOURCE !== undefined) {
        SOURCE.setData(Data);
        const currentSource = this.registeredSources.value.find(s => s.name === id);
        const hasData = Data && ( (Data.features && Data.features.length > 0) || (Data.type === 'Feature' && Data.geometry) );
        const canBeSearched = hasData && Array.isArray(Data.features) && 
          Data.features.some(f => f.properties && 
            Object.keys(f.properties).length > 0 && 
            (f.properties[currentSource.displayAttribute] || f.properties._Id ));

        this.updateSourceDataStatus(id, hasData, canBeSearched);
      }
    } catch (error) {
      if(!environment.production) console.log(error);
    }
  }

  UpdateLayerText(id: string, textoNuevo: any) {
    this._map.setLayoutProperty(id, "text-field", textoNuevo);
  }

  UpdateActiveLocation(obj: { lat: number; lng: number, idntf: string, idLocationCategory: number, locationCategory: string, activeIcon: string }) {
    //todo: crear iconos de activo para cada tipo (offline,online,sr,categorias)
    //const properties = obj.activeIcon ? { LocationCategoryActiveIcon: obj.activeIcon } : {}; 
    var jsonf = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [obj.lng, obj.lat],
          },
          properties: {
            title: "test",
            _Id: obj.idntf,
            //...properties
          },
        },
      ],
    };

    console.log("active icon", jsonf);
    

    this.UpdateLayerGeoJson("location"+obj.locationCategory+"ActiveSource", jsonf);
    this.MoveLayerToTop("layer-location"+obj.locationCategory+"Active");
    // this.MoveLayer("layer-location"+obj.locationCategory+"Active", "layer-locationsGeneral");
  }

  UpdateActivePoint(lat: number, lng: number, sourceName: string, icon: string = null) {
    if(!environment.production) console.log("[UPDATE ACTIVE POINT] - BUFFER");
    var jsonf = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          properties: {
            title: "test",
            icon: icon
          },
        },
      ],
    };
    this.UpdateLayerGeoJson(sourceName, jsonf);
  }

  toggleTraficLayer(visibilityValue: string) {
    const trafficLayers = this._map.getStyle().layers.filter(layer => 
      layer.id.includes('traffic')
    );
    trafficLayers.forEach(layer => {
      this._map.setLayoutProperty(layer.id, 'visibility', visibilityValue);
    });
  }

  PrintLayers() {
   if(!environment.production) console.log(this._map.getStyle().layers);
  }

  layerReorganizationFirstIsLast(layerName: string[]) {
    layerName.forEach(layer => {
      this._map.moveLayer(layer);
    });
  }

  filterLayerByProperty(layerId: string, propertyName: string, propertyValues: string[]) {
    this._map.setFilter(layerId, ["in", 
      ["get", propertyName], 
      ['literal', propertyValues]
    ]);
  }

  removeFilterLayer(layerId: string) {
    this._map.setFilter(layerId, null);
  }

  //#endregion

  //#region ------------------ zona de sources -------------------------
  AddSourceGeoJson(name: string, Data: any, sourceDisplayName: string, icon: string) {
    var obj = {
      id: name,
      type: "geojson",
      data: Data,
    };
    this._map.addSource(name, obj);
    const hasData = Data && ( (Data.features && Data.features.length > 0) || (Data.type === 'Feature' && Data.geometry) );
    this.registerSource(name, 'geojson', sourceDisplayName, hasData, icon);
  }

  AddSourceClusteredEmptyGeoJson(name: string, sourceDisplayName: string, icon: string, promoteIdProperty?: string, displayAttribute: string = '', clusterMaxZoom: number = 14, clusterRadius: number = 50) {
    if(!environment.production) console.log("Adding source clustered empty geojson", name);
    var source = this._map.getSource(name);
    try {
      if (source === undefined) {
        var obj: any = {
          type: "geojson",
          // Use a URL for the value for the `data` property.
          data: this.Empty_GeoJson,
          cluster: true,
          clusterMaxZoom: clusterMaxZoom,
          clusterRadius: clusterRadius,
          generateId: true,
        };
        if (promoteIdProperty) {
          obj.promoteId = promoteIdProperty;
        }
        this._map.addSource(name, obj);
        this.registerSource(name, 'cluster-geojson', sourceDisplayName, false, icon, displayAttribute);
      }
    } catch (e) {}
  }

  AddSourceEmptyGeoJson(name: string, sourceDisplayName: string, icon: string, promoteIdProperty?: string, displayAttribute: string = '') {
    var source = this._map.getSource(name);
    try {
      if (source === undefined) {
        var obj: any = {
          type: "geojson",
          // Use a URL for the value for the `data` property.
          data: this.Empty_GeoJson,
        };
        if (promoteIdProperty) {
          obj.promoteId = promoteIdProperty;
        }
        this._map.addSource(name, obj);
        this.registerSource(name, 'geojson', sourceDisplayName, false, icon, displayAttribute);
      }
    } catch (e) {}
  }

  AddSourceHighEmptyGeoJson(name: string, sourceDisplayName: string, icon: string, displayAttribute: string = '') {
    var source = this._map.getSource(name);
    try {
      if (source === undefined) {
        var obj = {
          type: "geojson",
          // Use a URL for the value for the `data` property.
          data: this.Empty_GeoJson,
        };
        this._map.addSource(name, obj);
        this.registerSource(name, 'geojson', sourceDisplayName, false, icon, displayAttribute);
      }
    } catch (e) {}
  }

  //#endregion

  //#region ------------------- manipulacion de sources ----------------------

  GetSourceByName(name: string): mapboxgl.GeoJSONSource | undefined {
    return this._map.getSource(name) as mapboxgl.GeoJSONSource;
  }

  isSourceEmpty(sourceName: string) {
    var source = this._map.getSource(sourceName);
    if(!environment.production) console.log('source', source._data);
    

    if (source) {
      
      if (source._data.features.length === 0) {
        if(!environment.production) console.log('El source está vacío');
        return true;
      } else {
        if(!environment.production) console.log('El source tiene features');
        return false;
      }
    } else {
      console.error('Source no encontrado');
      return true;
    }
  }

  //#endregion
  
  //#region --------------- funciones de ayuda -------------- 

  CreateExternalLayersHoverEvent(capas: LayerDTO[]) {
    var hoveredMarker = null;
    var popup = new mapboxgl.Popup({});

    capas.forEach((capa) => {
      const showPopup = (e) => {
        const coordinates = (e.features[0].geometry).coordinates.slice();
        const properties = e.features[0].properties;
        if(!environment.production) console.log("Creating hover event for layer", properties);

        // Obtener ID del marcador
        const marker = properties;
        if(!environment.production) console.log("markerId", marker, e.features[0]);

        hoveredMarker = marker;

        // Cambiar icono del marcador
        // this._map.setLayoutProperty(capa.name + "-layer", "icon-image", [
        //   "match",
        //   ["get", hoveredMarkerId],
        //   hoveredMarkerId,
        //   capa.icon.replace(".png", "A.png"),
        //   capa.icon,
        // ]);
        // Cambiar texto del marcador
        this._map.setPaintProperty(capa.name + "textLayer", "text-color", [
          "match",
          ["get", "Id"],
          [hoveredMarker.Id], "#0F010F",
          "#530aa8"
        ]);

        var totalProperties = properties.length;
        var propertiesPerColumn = Math.ceil(totalProperties / 2);

        var table = '<table style="width: 100%;"><tbody>';

        // Iterar sobre las propiedades y agregarlas a la tabla en dos columnas
        var count = 0;
        for (const key in properties) {
          const value = properties[key];
          // Si se llenó la primera parte de la tabla, cerrarla y abrir una nueva (de momento, la segunda tabla está directamente abajo de la primera)
          if (count === propertiesPerColumn) {
            table += '</tbody></table><table style="width: 100%;"><tbody>';
          }
          // Agregar la propiedad y su valor como una fila en la tabla
          table += `<tr><td style="border-bottom: 1px solid black; padding-right: 10px;">${key}</td><td style="border-bottom: 1px solid black; padding-left: 10px;">${value}</td></tr>`;
          count++;
        }
        
        table += "</tbody></table>";

        // determina el anchor del popup y el offset según el anchor
        const mapCenter = this._map.getCenter();
        const anchor = coordinates[0] > mapCenter.lng ? "right" : "left";
        const offsetX = anchor === "left" ? 15 : -15;

        // Mostrar el popup con la tabla
        popup = new mapboxgl.Popup({ anchor: anchor, offset: [offsetX, 0], closeButton: false, className: 'table-popup'})
          .setLngLat(coordinates)
          .setHTML(`<h3 style="margin: 0px 30%;">Propiedades</h3>` + table)
          .addTo(this._map);
      };

      const hidePopUp = () => {
        
        // Restaurar icono del marcador solo si hay un marcador bajo el mouse
        if (hoveredMarker) {
          this._map.setLayoutProperty(
            capa.name + "-layerUnclustered",
            "icon-image",
            capa.icon
          );
          this._map.setPaintProperty(
            capa.name + "textLayer",
            "text-color",
            "#530aa8"
          );
          hoveredMarker = null;
        }

        // Cerrar popup si está abierto
        if (popup) {
          popup.remove();
          popup = null;
        }
         
      }

      // Evento mouseenter
      this._map.off("mouseenter", capa.name + "-layerUnclustered", showPopup);
      this._map.off("mouseenter", capa.name + "textLayer", showPopup);
      this._map.on("mouseenter", capa.name + "-layerUnclustered", showPopup);
      this._map.on("mouseenter", capa.name + "textLayer", showPopup);

      // Evento mouseleave
      this._map.off("mouseleave", capa.name + "-layerUnclustered", hidePopUp);
      this._map.off("mouseleave", capa.name + "textLayer", hidePopUp);
      this._map.on("mouseleave", capa.name + "-layerUnclustered", hidePopUp);
      this._map.on("mouseleave", capa.name + "textLayer", hidePopUp);

      
    });
  }

  getpolygonPointsCentroid(polygonPoints: any[][]) {
    const coordinates = this.turfHelperService.getCentroidGeoJSON(polygonPoints).geometry.coordinates;
    return coordinates;
  }

  getpolygonCentroid(feature: any) {
    const newFeature = this.turfHelperService.getFeatureCentroidGeoJSON(feature);
    return newFeature;
  }

  // limpia los popup
  clearPopupEvents(mainClassName: string) {
    if (this._map) {
      const popups = document.getElementsByClassName('mapboxgl-popup');
      if (popups.length > 0) {
        Array.from(popups).forEach(popup => {
          if (popup.classList.contains(mainClassName)) {
            popup.remove();
          }
        });
      }
    }
  }
  
  //#endregion

//#region ------------------ controles del mapa -------------------------
  AddMapboxPlaces() {
    if (window.innerWidth >= 750) {
      this.geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Búsqueda por Dirección",
      });
      // Add the control to the map.
      this._map.addControl(this.geocoder);
    }
  }

  RemoveMapboxPlaces() {
    // remueve el geocoder
    this._map.removeControl(this.geocoder);
    this.geocoder = null;
  }

  AddControlNavigation() {
    if (this._containsControlNavigation == true) {
      this._map.addControl(new mapboxgl.NavigationControl());
      this._containsControlNavigation = true;
    }
  }

  AddControlFullScreen() {
    if (this._containsControlFullScreen == true) {
      this._map.addControl(new mapboxgl.FullscreenControl());
      this._containsControlFullScreen = true;
    }
  }

  AddControlGeolocalization() {
    if (this._containsControlGeolocalization == true) {
      {
        this._map.addControl(
          new mapboxgl.GeolocateControl({
            positionOptions: {
              enableHighAccuracy: true,
            },
            trackUserLocation: true,
          })
        );
        this._containsControlGeolocalization = true;
      }
    }
  }

  //#endregion
  

  //#region ------------------ funciones de cámara -------------------------

  startTour() {
    this.InitialFly( "19.38463", "-99.09985", "10", 30 );

      setTimeout(() => {
        this.InitialFly( "19.38463", "-99.09589", "11", 75 );
    
        setTimeout(() => {
          this.TourCamera(7000);
        }, 1050);
    
      }, 2000);
  }

  fitBoundsToMexico(){
    if (!this._map) {
      return;
    }
    const bounds = [
      [-117.1661, 14.5345],
      [-86.8116, 32.7187],
    ];
    this._map.fitBounds(bounds, { padding: 10, duration: 500, pitch: 0 });
  }

  SetBounds(sGeoJSONFeatures: any) {
    var b2 = this.turfHelperService.getBounds(sGeoJSONFeatures);
    this._map.fitBounds(b2, { padding: 250 });
  }

  FitBounds(geometry: any, padding: number = 100, pitch: number = 0) {
    if(!environment.production) console.log("FIT BOUNDS", geometry);
    let coordenadas: [number, number][] = [];

    if( geometry.type == "FeatureCollection" ){
      if( geometry.features.length == 0 )
        return;

      coordenadas = [];
      geometry.features.forEach( feature => {
        coordenadas.push( ...this.getFitBoundsCoords( feature.geometry ) );
      });
    }else{
      coordenadas = this.getFitBoundsCoords( geometry );
    }
      if(!environment.production) console.log("COORDENADAS", coordenadas);
  
      // Crea el bounds con las coordenadas iniciales
      const bounds = new mapboxgl.LngLatBounds(coordenadas[0], coordenadas[0]);
  
      if(!environment.production) console.log("BOUNDS", bounds);
  
      // Extiende el bounds para que tenga todas las coordenadas.
      for (const coord of coordenadas) {
        bounds.extend(coord);
      }

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      if(!environment.production) console.log("SW", sw);
      if(!environment.production) console.log("NE", ne);
      var isPunto = (sw.lng == ne.lng) && (sw.lat == ne.lat);
      if( isPunto ){
        const extender = 0.0005;
        bounds.setSouthWest( new mapboxgl.LngLat( sw.lng - extender, sw.lat - extender ) );
        bounds.setNorthEast( new mapboxgl.LngLat( ne.lng + extender, ne.lat + extender ) );
      }
  
      const bb = [
        [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
        [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
      ];

      const mapWidth = this._map.getContainer().clientWidth;
      const mapHeight = this._map.getContainer().clientHeight;
      const safeH = Math.floor(Math.max(0, (mapHeight / 3) - 10));
      const safeW = Math.floor(Math.max(0, (mapWidth / 3) - 10));
      const padTop    = Math.min(padding, safeH);
      const padBottom = Math.min(padding, safeH);
      const padLeft   = Math.min(padding + 200, safeW);
      const padRight  = Math.min(padding + 100, safeW);
      
      this._map.setPitch(pitch);
      this._map.fitBounds(bb, {
          padding: {top: padTop, bottom: padBottom, left: padLeft, right: padRight},
      });
      
    
  }

  getFitBoundsCoords(geometry: any): [number, number][] {
    if(!environment.production) console.log("fitbounds GEOMETRY", geometry);
    
    let coordenadas: [number, number][] = [];
    if( geometry.type == "Point" ){
      var coordenada: [number, number] = geometry.coordinates as [number, number];
      coordenadas.push( coordenada );
    }

    if( geometry.type == "Polygon" ){
      var coordenadasPoligono: [number, number][] = geometry.coordinates[0];
      coordenadas.push(...coordenadasPoligono);
    }

    if( geometry.type == "LineString" ){
      coordenadas.push(...geometry.coordinates as [number, number][]);
    }

    //el multi tiene tres arrays el primero es de poligonos, el segundo es el de anillos y el tercero es el de puntos. 
    // el anillo exterior es el bueno y el resto es de anillos interiores que no importan ahorita
    //asi que el [ [ [ [lng, lat], [lng, lat] ] ], [ [ [lng, lat], [lng, lat] ] ] ]
    // o [ Poligono1[ Anillo1[ Puntos[Lng, Lat] ], Anillo2[ Puntos[Lng, Lat] ] ] , Poligono2[ Anillo1[ Puntos[Lng, Lat] ] ] ]  help
    if( geometry.type == "MultiPolygon" ){
      var poligonos: [number, number][][][] = geometry.coordinates;
      poligonos.forEach( poligono => {
        var coordenadasPoligono: [number, number][] = poligono[0]; // solo saco el 0 porque es la zona exterior del poligono, el resto no importa
        coordenadas.push(...coordenadasPoligono);
      });
    }

    if(!environment.production) console.log("FIT BOUNDS COORDENADAS", coordenadas);

    return coordenadas;
  }

  InitialFly(lat: string, lng: string, zoom: string, pitch: number) {
    setTimeout( () => {
      this._map.flyTo({
        center: [lng, lat],
        zoom: zoom,
        // These options control the flight curve, making it move
        // slowly and zoom out almost completely before starting
        // to pan.
        speed: 1.5, // make the flying slow
        curve: 1, // change the speed at which it zooms out
        pitch: pitch,

        // This can be any easing function: it takes a number between
        // 0 and 1 and returns another number between 0 and 1.
        easing: function (t) {
          return t;
        },

        // this animation is considered essential with respect to prefers-reduced-motion
        essential: true,
      });
      // this._map.rotateCamera(0);
    }, 1000);
  }

  TourCamera(timestamp: number): void {
    setTimeout( () => {
      this._map.rotateTo(0, { duration: 3000 });
    }, 1000);
    setTimeout( () => {
      this._map.rotateTo(180, { duration: 3000 });
    }, 4000);
    setTimeout( () => {
      this._map.rotateTo(359, { duration: 3000 });
    }, 7000);
  }

  FlyToCameraFast(lat: string, lng: string, zoom: string, pitch: number = 0) {
    if(!environment.production) console.log("MAP", this._map);
    this._map.flyTo({
      center: [lng, lat],
      pitch: pitch,
      zoom: zoom,
      // These options control the flight curve, making it move
      // slowly and zoom out almost completely before starting
      // to pan.
      speed: 3, // make the flying slow
      curve: 1, // change the speed at which it zooms out

      // This can be any easing function: it takes a number between
      // 0 and 1 and returns another number between 0 and 1.
      easing: function (t) {
        return t;
      },

      // this animation is considered essential with respect to prefers-reduced-motion
      essential: true,
    });
  }

  FlyToCamera(lat: string, lng: string, zoom: string, pitch: number = 0) {
    this._map.flyTo({
      center: [lng, lat],
      pitch: pitch,
      zoom: zoom,
      // These options control the flight curve, making it move
      // slowly and zoom out almost completely before starting
      // to pan.
      speed: 2, // make the flying slow
      curve: 1, // change the speed at which it zooms out

      // This can be any easing function: it takes a number between
      // 0 and 1 and returns another number between 0 and 1.
      // easing: function (t) {
      //   return t;
      // },

      // this animation is considered essential with respect to prefers-reduced-motion
      essential: true,
    });
  }

  RotationCamera() {
    setTimeout(function () {
      this.rotateCamera(0);
    }, 20000);
  }

  rotateCamera(timestamp): void {
    // clamp the rotation between 0 -360 degrees
    // Divide timestamp by 100 to slow rotation to ~10 degrees / sec
    this._map.rotateTo((timestamp / 100) % 360, { duration: 0 });
    // Request the next frame of the animation.
    requestAnimationFrame(this.rotateCamera);
  }

  //#endregion
  
  //#region ------------------ puntos que se ajustan a la callae (no se usa de momento) -------------------------
  
  async AddMapMatchedRoute(id: string, coordinates: number[][], lineColor: string = "#352F84") {
    try {
      if(!environment.production) console.log('Map Matched Route with coordinates:', coordinates);
      
      //no se hace el map match de momento, cambia a true si se quiere hacer el map match
      if (false) {
        // 100 es el máximo, si hay más los simplifica
        if (coordinates.length > 100) {
          coordinates = this.simplifyCoordinatesForMapMatching(coordinates, 100);
        }
        
        const coordsString = coordinates.map(coord => `${coord[0]},${coord[1]}`).join(';');
        const response = await fetch(
          `https://api.mapbox.com/matching/v5/mapbox/driving/${coordsString}?geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`
        );

          const data = await response.json();
          if(!environment.production) console.log('Map matched route data:', data);
          
          if (data.matchings && data.matchings.length > 0) {
            const matchedRoute = {
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                geometry: data.matchings[0].geometry,
                properties: {
                  confidence: data.matchings[0].confidence
                }
              }]
            };
            const routeCoordinates = data.matchings[0].geometry.coordinates;
  
            const routeStart = {
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: routeCoordinates[0]
                },
                properties: {
                  confidence: data.matchings[0].confidence
                }
              }]
            };
  
            const routeEnd = {
              type: "FeatureCollection",
              features: [{
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: routeCoordinates[routeCoordinates.length - 1]
                },
                properties: {
                  confidence: data.matchings[0].confidence
                }
              }]
            };
  
            const routeDialog = {
              "type": "FeatureCollection",
              "features": [
                {
                  "type": "Feature",
                  "geometry": {
                    "type": "Point",
                    "coordinates": routeCoordinates[Math.ceil(routeCoordinates.length / 5)]
                  },
                  "properties": {
                    "RTA": "15 horas 12 minutos",
                  }
                }
              ]
            };
  
            this.UpdateLayerGeoJson(id, matchedRoute);
            this.UpdateLayerGeoJson(id + "-Start", routeStart);
            this.UpdateLayerGeoJson(id + "-End", routeEnd);
            this.UpdateLayerGeoJson(id + "-Dialog", routeDialog);
            return;
          }
        
      }

      const directRoute = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: coordinates
          },
          properties: {}
        }]
      };

      const routeStart = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates[0]
          },
          properties: {
            type: "start",
            title: "Punto de inicio"
          }
        }]
      };
  
      const routeEnd = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates[coordinates.length - 1]
          },
          properties: {
            type: "end",
            title: "Punto final"
          }
        }]
      };

      const routeDialog = {
            "type": "FeatureCollection",
            "features": [
              // {
              //   "type": "Feature",
              //   "geometry": {
              //     "type": "Point",
              //     "coordinates": coordinates[Math.ceil(coordinates.length / 5)]
              //   },
              //   "properties": {
              //     "RTA": "15 horas 12 minutos",
              //   }
              // }
            ]
          };
      
      this.UpdateLayerGeoJson(id, directRoute);
      this.UpdateLayerGeoJson(id + "-Start", routeStart);
      this.UpdateLayerGeoJson(id + "-End", routeEnd);
      this.UpdateLayerGeoJson(id + "-Dialog", routeDialog);

      // si no acepta los 100, usa la que separa de 10 en 10  REVISAR SI AFECTA PRECIOS
      // await this.AddSnapToRoadsRoute(id, coordinates);

    } catch (error) {
      const directRoute = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: coordinates
          },
          properties: {}
        }]
      };

      const routeStart = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates[0]
          },
          properties: {
            type: "start",
            title: "Punto de inicio"
          }
        }]
      };
  
      const routeEnd = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates[coordinates.length - 1]
          },
          properties: {
            type: "end",
            title: "Punto final"
          }
        }]
      };

      const routeDialog = {
            "type": "FeatureCollection",
            "features": [
              // {
              //   "type": "Feature",
              //   "geometry": {
              //     "type": "Point",
              //     "coordinates": coordinates[Math.ceil(coordinates.length / 5)]
              //   },
              //   "properties": {
              //     "RTA": "15 horas 12 minutos",
              //   }
              // }
            ]
          };
      
      this.UpdateLayerGeoJson(id, directRoute);
      this.UpdateLayerGeoJson(id + "-Start", routeStart);
      this.UpdateLayerGeoJson(id + "-End", routeEnd);
      this.UpdateLayerGeoJson(id + "-Dialog", routeDialog);
    }
  }
  
  // otra funcion que ajusta puntos a calles usando match pequeños
  async AddSnapToRoadsRoute(id: string, coordinates: number[][]) {
    try {
      const snappedCoordinates: number[][] = [];
      
      // procesa en grupos más pequeños para evitar problemas de límite de puntos
      const pointsGroupSize = 10;
      for (let i = 0; i < coordinates.length; i += pointsGroupSize) {
        const pointsGroup = coordinates.slice(i, i + pointsGroupSize);
        const snappedPointGroup = await this.matchCoordsToStreet(pointsGroup);

        if (i > 0) {
          // Evitar duplicar el primer punto del grupo
          snappedCoordinates.push(...snappedPointGroup.slice(1));
        } else {
          snappedCoordinates.push(...snappedPointGroup);
        }
        
        // se epera un poco para evitar problemas de límite de solicitudes
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      if(!environment.production) console.log('Map matched data piezas:', snappedCoordinates);
      const snappedRoute = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: snappedCoordinates
          },
          properties: {}
        }]
      };
      
      this.UpdateLayerGeoJson(id, snappedRoute);
      
    } catch (error) {
      const directRoute = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: coordinates
          },
          properties: {}
        }]
      };
      
      this.UpdateLayerGeoJson(id, directRoute);
    }
  }
  
  private async matchCoordsToStreet(coordinates: number[][]): Promise<number[][]> {
    if (coordinates.length < 2) return coordinates;
    
    const coordsString = coordinates.map(coord => `${coord[0]},${coord[1]}`).join(';');
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/matching/v5/mapbox/driving/${coordsString}?geometries=geojson&overview=full&radiuses=${coordinates.map(() => '25').join(';')}&access_token=${mapboxgl.accessToken}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.matchings && data.matchings.length > 0) {
          return data.matchings[0].geometry.coordinates;
        }
      }
    } catch (error) {
    }
    
    return coordinates;
  }
  
  private simplifyCoordinatesForMapMatching(coordinates: number[][], maxPoints: number): number[][] {
    if (coordinates.length <= maxPoints) 
      return coordinates;
    
    // formula de internet "douglas-pecker" para simplificar puntos
    return this.mapUtilService.simplifyCoordinatesAdvanced(coordinates, 0.00005);
  }

  //#endregion


  //#region ------------------ asset animado -------------------------
  dotSize = 200;
  
  // This implements `StyleImageInterface`
  // to draw a pulsing dot icon on the map.
   pulsingDot = {
    width: this.dotSize,
    height: this.dotSize,
    data: new Uint8Array(this.dotSize * this.dotSize * 4),
  
    // When the layer is added to the map,
    // get the rendering context for the map canvas.
    onAdd: function (map) {
      this.map = map;
      const canvas = document.createElement("canvas");
      canvas.width = this.width;
      canvas.height = this.height;
      this.context = canvas.getContext("2d");
    },
  
    // Call once before every frame where the icon will be used.
    render: function () {
      const duration = 2000;
      const t = (performance.now() % duration) / duration;
  
      const radius = (this.dotSize / 2) * 0.5;
      const outerRadius = (this.dotSize / 2) * 0.7 * t + radius;
      const context = this.context;
  
      // Draw the outer circle.
      context.clearRect(0, 0, this.width, this.height);
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      context.fillStyle = `rgba(76, 243, 255, ${1 - t})`;
      context.fill();
  
      // Draw the inner circle.
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
      context.fillStyle = "rgb(96, 244, 255)";
      context.strokeStyle = "white";
      context.lineWidth = 2 + 4 * (1 - t);
      context.fill();
      context.stroke();
  
      // Update this image's data with data from the canvas.
      this.data = context.getImageData(0, 0, this.width, this.height).data;
  
      // Continuously repaint the map, resulting
      // in the smooth animation of the dot.
      if (this.map) {
        this.map.triggerRepaint();
      }
  
      // Return `true` to let the map know that the image was updated.
      return true;
    },
  };

  //#endregion
}
